import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-client';
import { cancelAllUploads } from '@/features/vault/uploadManager';
import {
  getMe,
  isAdmin,
  login,
  logout,
  requestPasswordReset,
  resetPassword,
  signup,
  updateUiPrefs,
  type AuthMeResponse,
} from './api';

/** Exported so a component can ask whether anyone is signed in WITHOUT triggering a fetch
 *  (the login page must make no authed call). */
export const ME_KEY = ['auth', 'me'] as const;

/**
 * Look up the current signed-in user. Returns `null` data on 401 (anonymous);
 * the api-client's 401 interceptor normally redirects to /login, so this
 * hook only resolves to `null` when called via `enabled: false` on a public
 * page that handles unauthenticated state itself (login + signup).
 */
export function useMe(enabled = true) {
  return useQuery<AuthMeResponse | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await getMe();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    enabled,
    // Session + gate state must stay fresh so a revoked session (admin track
    // switch, logout/password-change elsewhere) or a re-gate is noticed PROMPTLY
    // — not after the global 5-min staleness, which left the user on stale pages
    // until a hard refresh. /me is a cheap endpoint: re-check on tab focus and on
    // a short interval (overrides the global staleTime/refetchOnWindowFocus=false).
    //
    // staleTime 15s (was 0): ~34 components consume this ONE query, so a
    // zero-second trust window re-fired it on every page navigation and tab
    // refocus on top of the 30s heartbeat (the "/me storm"). 15s dedups those
    // bursts without changing any detection path: own actions (login/logout/
    // terms/prefs) invalidate or write the cache explicitly; revocation kicks
    // via the api-client's global 401 interceptor on any request; the 30s
    // interval fires regardless of staleness; and a focus after any real
    // absence (>15s) still refetches immediately.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    retry: false,
  });
}

/**
 * ``true`` when the signed-in user is an admin. Returns ``false`` for
 * editor / viewer and during the loading window before ``useMe()``
 * resolves — components should treat the no-data state the same as
 * "no permission" (UI affordance disabled until confirmed admin).
 *
 * Drives every Edit / Archive / Submit / Run / Team affordance.
 */
export function useIsAdmin(): boolean {
  const me = useMe();
  return isAdmin(me.data);
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: login,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
}

export function useSignup() {
  return useMutation({ mutationFn: signup });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    // onSettled, not onSuccess: a sign-out that fails on the server STILL sends the person to
    // /login (see Header), so leaving on the success path only would keep the previous person's
    // uploads running — and their filenames on the login screen — exactly when the server is
    // already misbehaving.
    onSettled: () => {
      void queryClient.removeQueries({ queryKey: ME_KEY });
      // Vault uploads run outside the page, so they outlive a sign-out unless told otherwise:
      // signing out navigates rather than reloading. Left alone, the next person at this
      // machine saw the previous one's filenames on the login screen, and anything still
      // transferring was filed into THEIR vault when it finished.
      cancelAllUploads();
    },
  });
}

/**
 * Persist a cosmetic UI preference (sidebar collapse, …). The server
 * returns the refreshed /me payload, which replaces the cached one so
 * every consumer sees the new preference without a refetch. Callers
 * fire-and-forget — the UI flips its local state immediately and this
 * just makes it stick across reloads.
 */
export function useUpdateUiPrefs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUiPrefs,
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me);
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({ mutationFn: requestPasswordReset });
}

export function useResetPassword() {
  return useMutation({ mutationFn: resetPassword });
}
